import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CountryProvider } from '@/contexts/CountryContext';
import Navbar from '@/components/zera/Navbar';
import Footer from '@/components/zera/Footer';
import ZeraPartners from '@/components/zera/ZeraPartners';
import { blogPostsRomania, romaniaBlogCategories, type RomaniaBlogPost } from '@/data/blog.romania';
import { User, ArrowRight, Search } from 'lucide-react';

const RomaniaJournalInner: React.FC = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState<RomaniaBlogPost | null>(null);

  const filteredPosts = blogPostsRomania.filter((post) => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPostsRomania.find((p) => p.featured);
  const regularPosts = filteredPosts.filter((p) => !p.featured || activeCategory !== 'All');

  return (
    <div className="theme-romania min-h-screen bg-[#FAF8F3]">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-[#1A3B5C] pt-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-8 pb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Journal</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white leading-[1.1] mb-4">
            Romanian Travel Stories
          </h1>
          <p className="font-sans text-sm text-white/70 max-w-xl leading-relaxed">
            Insights, guides, and inspiration from our journeys across Romania.
          </p>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-12 border-b border-[#E8DFD0]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex flex-wrap gap-3">
              {romaniaBlogCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`font-sans text-[10px] tracking-[0.15em] uppercase px-5 py-2.5 border transition-all ${
                    activeCategory === cat
                      ? 'border-[#C9A961] bg-[#C9A961] text-white'
                      : 'border-[#E8DFD0] text-[#999] hover:border-[#C9A961]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-[#E8DFD0] font-sans text-sm text-[#1A3B5C] focus:outline-none focus:border-[#C9A961] bg-transparent"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {activeCategory === 'All' && !searchQuery && featuredPost && (
        <section className="py-16">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <button onClick={() => setSelectedPost(featuredPost)} className="w-full text-left group">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                  <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-4 left-4 bg-[#C9A961] px-4 py-2">
                    <span className="font-sans text-[9px] tracking-wider uppercase text-white">Featured</span>
                  </div>
                </div>
                <div className="bg-white p-10 lg:p-16 flex flex-col justify-center">
                  <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-4">{featuredPost.category}</span>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A3B5C] font-light mb-4 group-hover:text-[#C9A961] transition-colors">{featuredPost.title}</h2>
                  <p className="font-sans text-sm text-[#666] leading-relaxed mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center gap-4 text-[#999]">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" />
                      <span className="font-sans text-xs">{featuredPost.author}</span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="py-16">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-serif text-2xl text-[#1A3B5C] mb-2">No articles found</p>
              <p className="font-sans text-sm text-[#999]">Try adjusting your search or category filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(activeCategory === 'All' && !searchQuery ? regularPosts.filter((p) => !p.featured) : filteredPosts).map((post) => (
                <button key={post.id} onClick={() => setSelectedPost(post)} className="text-left group bg-white border border-[#E8DFD0] hover:border-[#C9A961]/30 transition-all duration-500 overflow-hidden">
                  <div className="relative aspect-[3/2] overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1">
                      <span className="font-sans text-[9px] tracking-wider uppercase text-[#C9A961]">{post.category}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-lg text-[#1A3B5C] font-light mb-2 group-hover:text-[#C9A961] transition-colors line-clamp-2">{post.title}</h3>
                    <p className="font-sans text-xs text-[#666] leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-[#999]">
                      <span className="font-sans text-[10px]">{post.date}</span>
                      <span className="font-sans text-[10px]">{post.author}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Article Modal */}
      {selectedPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto" onClick={() => setSelectedPost(null)}>
          <div className="bg-white max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video">
              <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-full object-cover" />
              <button onClick={() => setSelectedPost(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/90 flex items-center justify-center hover:bg-white transition-colors">
                <span className="text-[#1A3B5C] text-xl">&times;</span>
              </button>
            </div>
            <div className="p-10">
              <div className="flex items-center gap-4 mb-4">
                <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961]">{selectedPost.category}</span>
                <span className="font-sans text-[10px] text-[#999]">{selectedPost.date}</span>
              </div>
              <h2 className="font-serif text-3xl text-[#1A3B5C] font-light mb-4">{selectedPost.title}</h2>
              <div className="flex items-center gap-4 mb-8 text-[#999]">
                <div className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  <span className="font-sans text-xs">{selectedPost.author}</span>
                </div>
              </div>
              <div className="prose prose-sm max-w-none">
                <p className="font-sans text-sm text-[#666] leading-relaxed mb-4">
                  Romania continues to captivate travelers who venture beyond the well-worn paths of European tourism. With its dramatic Carpathian peaks, medieval citadels, and warm village hospitality, this corner of Eastern Europe offers experiences that rival, and often surpass, its more famous neighbors.
                </p>
                <p className="font-sans text-sm text-[#666] leading-relaxed mb-4">
                  Whether you're drawn to the Gothic legend of Bran Castle, the vivid frescoes of Bucovina's painted monasteries, or the wild channels of the Danube Delta, there's a story waiting to be discovered around every corner.
                </p>
                <p className="font-sans text-sm text-[#666] leading-relaxed">
                  At Zera Travel, we believe the best stories are the ones you live. Let us help you write yours in Romania.
                </p>
              </div>

              <div className="mt-8 pt-8 border-t border-[#E8DFD0]">
                <button onClick={() => { setSelectedPost(null); navigate('/romania/booking'); }} className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] flex items-center gap-2 hover:gap-3 transition-all">
                  Plan Your Journey <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gallery strip */}
      <section className="relative w-full overflow-hidden bg-[#1A3B5C]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-16 pb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">Gallery</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-white leading-[1.1] mb-3">
            Every Story Has a Place
          </h2>
          <p className="font-sans text-sm text-white/70 max-w-xl leading-relaxed">
            A visual index of every destination featured in our Romanian journal.
          </p>
        </div>

        <div aria-hidden="true" className="relative w-full overflow-hidden select-none pointer-events-none">
          <div className="flex w-max gap-3 animate-[romania-journal-scroll_90s_linear_infinite]">
            {[...blogPostsRomania, ...blogPostsRomania].map((post, idx) => (
              <div key={`${post.id}-${idx}`} className="relative shrink-0 w-[360px] md:w-[480px] aspect-[3/2] overflow-hidden">
                <img src={post.image} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50" />
              </div>
            ))}
          </div>

          <style>{`
            @keyframes romania-journal-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `}</style>
        </div>
      </section>

      <ZeraPartners />
      <Footer />
    </div>
  );
};

const RomaniaJournal: React.FC = () => (
  <CountryProvider country="romania">
    <RomaniaJournalInner />
  </CountryProvider>
);

export default RomaniaJournal;
